import { useMutation } from '@tanstack/react-query';
import { FC, createContext, ReactNode, useCallback, useContext, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store';
import { clearShopSession, setShops, switchShopComplete } from '@/features/shops/model/shops.slice';
import { setUser } from '@/features/users/current-user.slice';
import { api } from '@/services/api';
import { authApi } from '@/services/auth/api';
import {
  clearAuthTokens,
  getAccessToken,
  loginWithPassword,
  setAuthInitialized,
  setAuthenticated,
} from '@/services/auth';
import { shopApi } from '@/services/shopApi';
import { closeSocket, initSocket } from '@/services/support-chat';
import { clearBrowserStorage } from '@/shared/lib/clear-browser-storage';
import { EmployeeRole } from '@/shared/model/access';
import { registerWithInvite } from '@/services/auth/http-client';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  registerUser: (payload: RegisterRequestType) => Promise<boolean>;
  logout: () => void;
  isPending: boolean;
}

interface LoginRequestType {
  email: string;
  password: string;
}

interface RegisterRequestType {
  token: string;
  first_name: string;
  last_name: string;
  password: string;
  patronymic?: string | null;
  phone?: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
let sessionInitializationPromise: Promise<void> | null = null;

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isInitialized = useAppSelector((state) => state.auth.isInitialized);
  const baseUrl: string = import.meta.env.VITE_IOKA_API_URL;
  const apiKey = useAppSelector((state) => state.shops.apiKey);

  const dispatch = useAppDispatch();

  // Инициализируем глобальный WebSocket
  useEffect(() => {
    if (!apiKey) return;

    (async () => {
      try {
        await closeSocket();
      } catch {
        /* ignore */
      }

      const wsUrl = baseUrl.replace(/^http/, 'ws') + '/v2/support/chat/ws';

      try {
        initSocket(wsUrl, apiKey);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to init chat socket:', e);
      }
    })();
  }, [apiKey, baseUrl]);

  const clearSessionState = useCallback(() => {
    localStorage.removeItem('selectedShopId');
    dispatch(clearShopSession());
    dispatch(setUser(null));
    dispatch(api.util.resetApiState());
  }, [dispatch]);

  const bootstrapSwitchSession = useCallback(
    async (preferredShopId?: string | null) => {
      const meRequest = dispatch(
        authApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }),
      );
      const shopsRequest = dispatch(
        shopApi.endpoints.getShops.initiate(undefined, { forceRefetch: true }),
      );

      const [user, shops] = await Promise.all([meRequest.unwrap(), shopsRequest.unwrap()]).finally(
        () => {
          meRequest.unsubscribe();
          shopsRequest.unsubscribe();
        },
      );

      dispatch(setUser(user));
      dispatch(setShops(shops));

      const isPreferredShopAvailable =
        !!preferredShopId && shops.some((shop) => shop.id === preferredShopId);
      let targetShopId = isPreferredShopAvailable ? preferredShopId : shops[0]?.id;

      if (user.is_superuser && !isPreferredShopAvailable) {
        targetShopId = 'all';
      }

      if (!targetShopId) {
        throw new Error('Shop is missing');
      }

      const switchRequest = dispatch(
        shopApi.endpoints.switchShop.initiate(
          targetShopId === 'all' ? undefined : { shopId: targetShopId },
        ),
      );
      const switchedSession = await switchRequest.unwrap().finally(() => {
        switchRequest.reset();
      });

      dispatch(
        switchShopComplete({
          shopId: targetShopId,
          apiKey: switchedSession.access_token,
          role:
            switchedSession.role ??
            (targetShopId === 'all' ? EmployeeRole.Owner : EmployeeRole.Viewer),
          refreshToken: switchedSession.refresh_token,
        }),
      );
      localStorage.setItem('selectedShopId', targetShopId);
    },
    [dispatch],
  );

  useEffect(() => {
    let isCancelled = false;

    const initializeSession = async () => {
      const token = getAccessToken();

      if (!token) {
        dispatch(setAuthenticated(false));
        dispatch(setAuthInitialized(true));
        return;
      }

      dispatch(setAuthInitialized(false));

      try {
        if (!sessionInitializationPromise) {
          sessionInitializationPromise = bootstrapSwitchSession(
            localStorage.getItem('selectedShopId'),
          ).finally(() => {
            sessionInitializationPromise = null;
          });
        }

        await sessionInitializationPromise;

        if (!isCancelled) {
          dispatch(setAuthenticated(true));
        }
      } catch (error) {
        if (!isCancelled) {
          clearAuthTokens();
          clearSessionState();
          dispatch(setAuthenticated(false));
        }

        // eslint-disable-next-line no-console
        console.error('Session initialization failed:', error);
      } finally {
        if (!isCancelled) {
          dispatch(setAuthInitialized(true));
        }
      }
    };

    initializeSession();

    return () => {
      isCancelled = true;
    };
  }, [bootstrapSwitchSession, clearSessionState, dispatch]);

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: LoginRequestType) => {
      dispatch(setAuthInitialized(false));
      clearSessionState();
      await loginWithPassword(email, password);
      await bootstrapSwitchSession();
      return true;
    },
    onSuccess: () => {
      dispatch(setAuthenticated(true));
      dispatch(setAuthInitialized(true));
    },
    onError: (error) => {
      clearAuthTokens();
      clearSessionState();
      dispatch(setAuthenticated(false));
      dispatch(setAuthInitialized(true));
      console.error('Login failed:', error);
      throw error;
    },
  });

  const registerMutation = useMutation({
    mutationFn: async ({
      token,
      first_name,
      last_name,
      password,
      patronymic = null,
      phone = null,
    }: RegisterRequestType) => {
      dispatch(setAuthInitialized(false));
      clearSessionState();
      await registerWithInvite({
        token,
        first_name,
        last_name,
        password,
        patronymic,
        phone,
      });
      await bootstrapSwitchSession();
      return true;
    },
    onSuccess: () => {
      dispatch(setAuthenticated(true));
      dispatch(setAuthInitialized(true));
    },
    onError: (error) => {
      clearAuthTokens();
      clearSessionState();
      dispatch(setAuthenticated(false));
      dispatch(setAuthInitialized(true));
      console.error('Registration failed:', error);
      throw error;
    },
  });

  const login = async (email: string, password: string) =>
    loginMutation.mutateAsync({ email, password });

  const registerUser = async (payload: RegisterRequestType) =>
    registerMutation.mutateAsync(payload);

  const { isPending } = loginMutation;
  const { isPending: isRegisterPending } = registerMutation;

  const logout = () => {
    try {
      closeSocket();
    } catch {
      // Socket cleanup must not block logout.
    }
    clearAuthTokens();
    clearSessionState();
    dispatch(setAuthenticated(false));
    dispatch(setAuthInitialized(true));

    (async () => {
      try {
        await clearBrowserStorage();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to clear browser cache on logout:', err);
      } finally {
        const url = `${window.location.origin}${window.location.pathname}`;
        window.location.replace(url);
      }
    })();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!isAuthenticated,
        login,
        registerUser,
        logout,
        isPending: isPending || isRegisterPending || !isInitialized,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
